import type { Scene } from "@babylonjs/core/scene";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { createSoftParticleTexture } from "../../core/particleTextures";
import { AccessibilitySystem } from "../../systems/AccessibilitySystem";
import { particleDensityFactor } from "../../systems/particleScaling";
import { shouldCullAmbient } from "../../systems/AmbientCull";
import { CAMERA } from "../../config/GameConfig";

type TextureLease = {
  system: ParticleSystem;
  texture: ReturnType<typeof createSoftParticleTexture>;
  released: boolean;
  observer: ReturnType<ParticleSystem["onDisposeObservable"]["addOnce"]>;
};

/** Local rendering owner. Existing stealth/cache systems keep their emitter,
 * emission, input and lifetime owners. Each borrowed system gets its own texture
 * so consuming a cache cannot dispose the other effects' texture. */
export class CanopyAtmosphere {
  readonly motes: ParticleSystem;
  private readonly leases = new Set<TextureLease>();
  private readonly sceneDispose;
  private disposed = false;
  private culled = false;

  constructor(private readonly scene: Scene, feedback: readonly ParticleSystem[]) {
    for (const ps of new Set(feedback)) if (!ps.particleTexture) this.texture(ps);
    this.motes = new ParticleSystem("canopyObservatoryMotes", 48, scene);
    this.texture(this.motes);
    this.motes.emitter = new Vector3(125, 9, -3);
    this.motes.createBoxEmitter(new Vector3(-.08,.02,0),new Vector3(.08,.1,.02),
      new Vector3(-8,-1.5,-.15),new Vector3(8,2,.15));
    this.motes.minLifeTime=2;this.motes.maxLifeTime=4;
    this.motes.minSize=.012;this.motes.maxSize=.035;
    this.motes.minEmitPower=.04;this.motes.maxEmitPower=.12;
    this.motes.color1=new Color4(.64,.73,.83,.14);
    this.motes.color2=new Color4(.78,.71,.55,.1);
    this.motes.colorDead=new Color4(.64,.73,.83,0);
    this.motes.blendMode=ParticleSystem.BLENDMODE_STANDARD;
    this.motes.start();
    this.sceneDispose=scene.onDisposeObservable.addOnce(()=>this.dispose());
    this.update(0,-16);
  }

  private texture(system: ParticleSystem): void {
    const texture=createSoftParticleTexture(this.scene,"canopySoft_"+system.name);
    const lease: TextureLease={system,texture,released:false,observer:null};
    // Babylon normally disposes its texture before notifying system observers.
    // Also cover dispose(false) without a duplicate release in the normal path.
    texture.onDisposeObservable.addOnce(()=>{lease.released=true;});
    system.particleTexture=texture;
    lease.observer=system.onDisposeObservable.addOnce(()=>this.release(lease));
    this.leases.add(lease);
  }

  private release(lease: TextureLease): void {
    lease.system.onDisposeObservable.remove(lease.observer);
    if(lease.system.particleTexture===lease.texture)lease.system.particleTexture=null;
    if(!lease.released)lease.texture.dispose();
    this.leases.delete(lease);
  }

  /** Invoked on the existing simulation clock; never changes feedback gates. */
  update(_dtMs: number, cameraZ: number): void {
    if(this.disposed)return;
    this.culled=shouldCullAmbient(Math.abs(cameraZ),CAMERA.AMBIENT_CULL_DISTANCE,CAMERA.AMBIENT_CULL_HYSTERESIS,this.culled);
    this.motes.emitRate=this.culled?0:4*particleDensityFactor(AccessibilitySystem.getInstance().state.reducedMotion);
  }

  dispose(): void {
    if(this.disposed)return;this.disposed=true;
    this.scene.onDisposeObservable.remove(this.sceneDispose);
    this.motes.stop();this.motes.dispose();
    for(const lease of [...this.leases])this.release(lease);
  }
}
